"use client";

import Image from "next/image";
import { ReactNode, useCallback, useRef } from "react";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import Container from "~/componentsDS/Layout/Container";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { cn } from "~/lib/utils";
import { getTextFromNode } from "~/utils/getTextFromNode";

const textCn = "text-center text-primary500";

const ctaText = "take the 2-min quiz";

export type NotAloneItem = {
    title: ReactNode;
    alt: string;
    titleClassName: string;
    imageDesktopUrl: string;
}

type EventData = {
    event_name: string;
    widget: string;
}

type Props = {
    className?: string;
    notAloneData: NotAloneItem[];
    eventData?: EventData;
}

const NotAloneSection = ({ className, notAloneData, eventData }: Props) => {
    const sendEvent = useTrackEvent();
    const prevIndexRef = useRef<number | null>(null);

    const handleCarouselSelect = useCallback(
        (api: CarouselApi) => {
            if (!api || !eventData) return;

            const index = api.selectedScrollSnap();
            if (prevIndexRef.current === index) return;

            const item = notAloneData[index];
            prevIndexRef.current = index;

            if (!item) return;

            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: eventData.event_name,
                    widget: eventData.widget,
                    meta_data: {
                        image_title: getTextFromNode(item.title),
                        image_count: index + 1,
                    },
                },
            });
        },
        [eventData, notAloneData, sendEvent]
    );

    return (
        <section className={cn("py-16 md:py-[96px]", className)}>
            <Container>
                <Reveal>
                    <Heading
                        as="h2"
                        size="sm"
                        sizes={{ md: "md" }}
                        className={cn(textCn, "mb-4 max-w-[300px] mx-auto md:max-w-content")}
                    >
                        You’re not managing this{" "}
                        <Heading
                            as="i"
                            font="featureDisplay"
                            weight="400"
                            className="text-[28px] md:text-[36px]"
                        >
                            alone
                        </Heading>
                    </Heading>
                </Reveal>
                <Paragraph size="lg" className={textCn}>Every prescription is reviewed and monitored by a licensed clinician.</Paragraph>
                <Paragraph size="lg" weight="500" className={cn(textCn, "mb-10 md:mb-8 max-md:mt-6")}>Hormones change. Your care adapts.</Paragraph>

                <div className="w-[100vw] md:w-full overflow-hidden mb-12 max-md:-mx-6 max-md:px-6">
                    <Carousel
                        loop={false}
                        variant="compact"
                        compactPaginationAlign="right"
                        compactPaginationBg="bg-trueBlack/10 backdrop-blur-[25px]"
                        compactPaginationDotColor="bg-gray100"
                        opts={{
                            align: "center",
                            skipSnaps: false,
                            dragFree: false
                        }}
                        onSelect={handleCarouselSelect}
                        className="w-full"
                    >
                        <CarouselContentVisible className="max-md:ml-[-6px] md:ml-[-18px]">
                            {notAloneData.map(({ title, alt, titleClassName, imageDesktopUrl }, index) => (
                                <CarouselItem key={index} className="pl-[6px] md:pl-[18px] flex-shrink-0 basis-full md:basis-1/2 xl:basis-1/3">
                                    <div className="relative h-[450px] md:h-[623px] rounded-[18px] overflow-hidden">
                                        <Heading
                                            font="featureDisplay"
                                            weight="400"
                                            className={cn(
                                                "relative z-[1]",
                                                "text-[28px] md:text-[31.5px] text-center !leading-[1.1]",
                                                "pt-10 md:pt-[54px] px-4",
                                                titleClassName
                                            )}
                                        >
                                            {title}
                                        </Heading>
                                        <Image
                                            src={imageDesktopUrl}
                                            alt={alt}
                                            fill
                                            sizes="800px"
                                            className="object-cover object-center pointer-events-none"
                                        />
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContentVisible>
                    </Carousel>
                </div>

                <div className="flex justify-center mt-8">
                    <QuizCTA
                        variant="primary"
                        eventData={eventData ? {
                            event_name: eventData.event_name,
                            widget: eventData.widget,
                            meta_data: {
                                click_text: ctaText,
                            },
                        } : undefined}
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        size="md"
                        className="max-md:w-full"
                    >
                        {ctaText}
                    </QuizCTA>
                </div>
            </Container>
        </section>
    )
};

export default NotAloneSection;
